import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist/types/src/display/api';
import { IExamInfo, mapToVanillaExamInfoForEncoding, toVanillaExamInfoForEncoding, QAVanillaIExamInfoForEncoding } from '@repositories/model/transfer/IExamInfo';
import NcodeAllocationRepository from '@repositories/NcodeAllocationRepository';
import { IBrushEnum, IPageSOBP, isSamePageOnlySOBP, NeoStroke } from '@neolab-libs/nl-lib3-common';
import { drawPath } from '../../../../../ndp/DrawCurves';
import { NcodeAllocationResponse } from '@repositories/model/transfer/INcodeAllocationResponse';

import { createImages } from '../../../../../ndp/SobpImageConverter';
import PdfToQAMapper from '@/common/objects/QuestionAnswerToPageMapping/PdfToQAMapper';

// if you already have a NeoStroke type:
export type QAEncodingType = Record<string, string[]>;

export default class GAIStrokeRecognizer {

	public ncodeRange: { start: IPageSOBP, end: IPageSOBP }
	public examInfoArray: IExamInfo[];
	public wholeStrokes: NeoStroke[];

	
	public async recognize() {

		// 1) classify strokes
		const grouped = this.classifyStrokes(this.wholeStrokes, this.ncodeRange, this.examInfoArray);

		// 2) generate images keyed by code (insertion order preserved)
		const images = await createImages({ codeToStrokes: grouped });

		// 2-1) optional: download images for testing
		/*
		try {
			if (typeof window !== 'undefined' && typeof document !== 'undefined') {
				const codes = Object.keys(images);
				for (const code of codes) {
					const filename = `gai-${code}.png`;
					this.downloadDataUrl(images[code].url, filename);
				}
			}
		} catch (e) {
			console.warn('Failed to auto-download test images:', e);
		} 
		*/
		
		// 3) recognize image and return (preserve order)
		const keys = Object.keys(images);
		const blobs: Blob[] = [];
		for (const code of keys) {
			try {
				const res = await fetch(images[code].url);
				const blob = await res.blob();
				blobs.push(blob);
			} catch (e) {
				console.warn('Failed to convert data URL to blob for code', code, e);
				blobs.push(new Blob());
			}
		}
		const codeToStrokes = await this.recognizeImages(blobs, undefined, keys);
		return codeToStrokes;
	}

	private classifyStrokes(wholeStrokes: NeoStroke[], ncodeRange: { start: IPageSOBP, end: IPageSOBP }, examInfoArray: IExamInfo[]) {

		const codeToStrokes = {};

		const sobp: IPageSOBP = {
			section: ncodeRange.start.section,
			owner: ncodeRange.start.owner,
			book: ncodeRange.start.book,
			page: ncodeRange.start.page
		};

		const questionToPageIndexMapping = PdfToQAMapper.readQuestionNameToPageIndex(examInfoArray);
		
		for (let examIndex = 0; examIndex < examInfoArray.length; examIndex++) {
			const type = examInfoArray[examIndex].type;
			const questions = examInfoArray[examIndex].questions;

			for (let questionIndex = 0; questionIndex < questions.length; questionIndex++) {
				let questionText = (examIndex + 1) + "-" + (questionIndex + 1);
				let code = examInfoArray[examIndex].code + "-" + (questionIndex + 1);

				const { page } = questionToPageIndexMapping[questionText];

				const contentSobp = { ...sobp, page: sobp.page + page };

				const strokes = wholeStrokes.filter((stroke) => isSamePageOnlySOBP(stroke.sobp, contentSobp));
				
				codeToStrokes[code] = [];
				
				for (const stroke of strokes) {
					if (stroke.dotArray[0]?.y > 24) {
						codeToStrokes[code].push(stroke);
					}

				}
			}
		}

		return codeToStrokes;
	}

	private downloadDataUrl(dataUrl: string, filename: string) {
		try {
			const a = document.createElement('a');
			a.href = dataUrl;
			a.download = filename;
			a.style.display = 'none';
			document.body.appendChild(a);
			a.click();
			setTimeout(() => {
				try { document.body.removeChild(a); } catch {}
			}, 0);
		} catch (e) {
			console.warn('downloadDataUrl failed', e);
		}
	}

	/**
	 * Send images to backend to recognize texts using AI. Returns mapping keyed by provided codes.
	 */
	private async recognizeImages(images: Blob[], languageCode?: string, keysInOrder?: string[]): Promise<Record<string, string>> {
		if (!images || images.length === 0) return {};
		const form = new FormData();
		images.forEach((blob, i) => {
			form.append('images', blob, `image_${i + 1}.png`);
		});
		if (keysInOrder && keysInOrder.length) {
			keysInOrder.forEach((k) => form.append('keys', k));
		} else {
			for (let i = 0; i < images.length; i++) form.append('keys', String(i));
		}
		if (languageCode) {
			form.append('languageCode', languageCode);
		}

		const res = await fetch('/api/v1/generativeAIUtil/image-to-strings', {
			method: 'POST',
			body: form,
		});
		if (!res.ok) {
			throw new Error(`Failed to recognize images: ${res.status} ${res.statusText}`);
		}
		const data = await res.json();
		return data as Record<string, string>;
	}
	
}

export class QAVanillaEncoder {

	static encodeInfo(examList: IExamInfo[]): QAEncodingType {
		       
		const vanillaExamInfos = mapToVanillaExamInfoForEncoding(examList);
		return QAVanillaEncoder.encodeInfoFromVanilla(vanillaExamInfos);
    }

	private static encodeInfoFromVanilla(examList: QAVanillaIExamInfoForEncoding[]): QAEncodingType {
		       
        const codeToStrokes = {};

        for (let examIndex = 0; examIndex < examList.length; examIndex++) {
            const numOfQuestions = examList[examIndex].numOfQuestions;

            for (let questionIndex = 0; questionIndex < numOfQuestions; questionIndex++) {
                let code = examList[examIndex].code + "-" + (questionIndex + 1);

                codeToStrokes[code] = [];
            }
        }

        return codeToStrokes;
    }
}

